import Tool, {
  type IToolEnabledInput,
  type IToolActionInput,
  type IToolAvailableInput,
} from './Tool';
import Serialization, {
  type CopiedWhiteboardContent,
} from '../collaboration/Serialization';
import DisableWhileUploadingBehavior from './DisableWhileUploadingBehavior';
import { isConnector, isShape, isSimpleSelection } from '../objects/guards';
import { type LabelObject } from '../objects/Connector';
import type WhiteboardController from '../controllers/WhiteboardController';
import Shortcut from '../utils/Shortcut';

export const copyWhiteboardContents = ({
  controller,
  selection,
}: {
  controller: WhiteboardController;
  selection: fabric.Object | fabric.Object[];
}): { objectsAsJson: CopiedWhiteboardContent | undefined } | undefined => {
  // Guard if nothing is selected on the whiteboard
  if (selection == null) return;

  const selectedObjects = (
    Array.isArray(selection)
      ? selection
      : isSimpleSelection(selection)
      ? selection.getObjects()
      : [selection]
  ).filter(object => {
    // Remove labels to ensure no label is copied without connector
    return !(
      isShape(object) &&
      object.shapeName === 'text' &&
      object.connectorId
    );
  });

  if (selectedObjects.length === 0) {
    // TODO some sort of error message when copying just a label. Could also
    // be resolved by preventing selection of individual labels and moving
    // all text formatting to connector for all labels on connector
    // Clear clipboard to prevent re-pasting previous copy
    return { objectsAsJson: undefined };
  }

  // Add labels via connector only
  const labels: LabelObject[] = [];
  selectedObjects.forEach(object => {
    if (isConnector(object)) {
      labels.push(...object.labels);
    }
  });
  selectedObjects.push(...labels);

  const objectsAsJson = Serialization.collectionToJSON(
    selectedObjects,
    controller.whiteboardId
  );

  return { objectsAsJson };
};

@DisableWhileUploadingBehavior
class ToolCopy extends Tool {
  static toolName = 'Copy objects';
  static shortcut = new Shortcut('Cmd+C');

  static action(input: IToolActionInput) {
    const result = copyWhiteboardContents(input);
    const { controller } = input;

    if (!result) {
      // don't override clipboard if nothing was selected
      return;
    }

    if (!result.objectsAsJson) {
      controller.actionSetClipboard('');
      void navigator.clipboard.writeText('');
    } else {
      controller.actionSetClipboard(result.objectsAsJson);
      // Write to the browser clipboard too.
      void navigator.clipboard.writeText(JSON.stringify(result.objectsAsJson));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static enabled(_: IToolEnabledInput) {
    return true;
  }

  static available({ controller, selection }: IToolAvailableInput) {
    // Copy not available if only comments are selected
    if (
      selection &&
      (selection.type === 'comment' ||
        (selection.type === 'activeSelection' &&
          selection.getObjects().every(obj => obj.type === 'comment')))
    ) {
      return false;
    }

    return super.available({ controller, selection });
  }
}

export default ToolCopy;
